import React, { useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useNavigate } from "react-router-dom";

const StudentViewInvoice = ({ invoices, outstanding }) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        enableSorting: true,
        enableHiding: true,
        size: 40,
        cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "invoiceStatus",
        enableHiding: true,
        size: 80,
        header: "Status",
        Cell: ({ row }) => {
          const status = row.original.invoiceStatus?.trim().toUpperCase();
          return status === "PAID" ? (
            <span className="badge bg-success fw-light">Paid</span>
          ) : status === "PARTIALLY_PAID" ? (
            <span className="badge bg-info fw-light">Partially Paid</span>
          ) : status === "CANCELLED" ? (
            <span className="badge bg-danger fw-light">Cancelled</span>
          ) : (
            <span className="badge bg-warning fw-light">Pending</span>
          );
        },
      },
      {
        accessorKey: "invoiceNumber",
        enableHiding: true,
        header: "Invoice Number",
        size: 120,
      },
      {
        accessorKey: "parent",
        enableHiding: true,
        header: "Parent",
        size: 100,
      },
      {
        accessorKey: "schedule",
        enableHiding: true,
        header: "Schedule",
        size: 150,
      },
      {
        accessorKey: "invoiceDate",
        enableHiding: true,
        header: "Invoice Date",
        size: 100,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "dueDate",
        enableHiding: true,
        header: "Due Date",
        size: 100,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "invoicePeriodFrom",
        enableHiding: true,
        header: "Period From",
        size: 100,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "invoicePeriodTo",
        enableHiding: true,
        header: "Period To",
        size: 100,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      // {
      //   accessorKey: "subTotal",
      //   enableHiding: true,
      //   header: "Sub Total",
      //   size: 80,
      //   Cell: ({ cell }) =>
      //     cell.getValue() ? `$${cell.getValue().toFixed(2)}` : " ",
      // },
      {
        accessorKey: "gst",
        enableHiding: true,
        header: "GST",
        size: 80,
        Cell: ({ cell }) =>
          cell.getValue() ? `$${cell.getValue().toFixed(2)}` : " ",
      },
      {
        accessorKey: "creditAdviceOffset",
        enableHiding: true,
        header: "Credit Offset",
        size: 80,
        Cell: ({ cell }) =>
          cell.getValue() ? `$${cell.getValue().toFixed(2)}` : "$0.00",
      },
      {
        accessorKey: "totalAmount",
        enableHiding: true,
        header: "Total Amount",
        size: 100,
        Cell: ({ cell }) => `$${cell.getValue().toFixed(2)}`,
      },
      {
        accessorKey: "noOfLessons",
        enableHiding: true,
        header: "No. of Lessons",
        size: 80,
      },
      {
        accessorKey: "remark",
        enableHiding: true,
        header: "Remark",
        size: 120,
        Cell: ({ cell }) => cell.getValue() || "",
      },
      {
        accessorKey: "createdAt",
        header: "Created On",
        size: 100,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "createdBy",
        header: "Created By",
        size: 100,
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        size: 100,
        Cell: ({ cell }) => formatDate(cell.getValue()) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        size: 100,
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ],
    []
  );

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: {
            backgroundColor: "#e0e0e0",
          },
          thumb: {
            color: "#eb862a",
          },
          switchBase: {
            "&.Mui-checked": {
              color: "#eb862a",
            },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  return (
    <>
      <div className="p-2">
        Outstanding Invoice Amount : SGD{" "}
        <span style={{ color: "#eb862a" }}> {outstanding || 0.0} </span>
      </div>
      <ThemeProvider theme={theme}>
        <MaterialReactTable
          columns={columns}
          data={invoices || []} // Use invoices prop with fallback to empty array
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          initialState={{
            columnVisibility: {
              createdBy: false,
              createdAt: false,
              updatedBy: false,
              updatedAt: false,
              remark: false,
              creditAdviceOffset: false,
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => navigate(`/invoice/view/${row.original.id}`),
            style: { cursor: "pointer" },
          })}
          renderTopToolbarCustomActions={() => (
            <>
              <button
                className="btn-button shadow-none"
                onClick={() => {
                  navigate(`/invoice/add`);
                }}
                style={{
                  borderRadius: "8px ",
                  fontSize: "10px",
                  boredr: "none",
                  outline: "none",
                }}
                type="button"
              >
                Create New Invoice
              </button>
            </>
          )}
          renderEmptyRowsFallback={() => (
            <div className="text-center py-4">No invoices available.</div>
          )}
        />
      </ThemeProvider>
    </>
  );
};

export default StudentViewInvoice;
